import React, {useState, useEffect} from 'react';
import axios from 'axios';
import GomokuBoard from './GomokuBoard';
import Button from "react-bootstrap/Button";
import whitePiece from "./whitePiece.svg";
import blackPiece from "./blackPiece.svg";
import './App.css';

type Board = Array<Array<number | null>>;

enum Piece {
	Black = 1,
	White = 2,
	HoverBlack = 3,
	HoverWhite = 4,
}

const App = () => {
	const BOARD_SIZE = 15;
  const CELL_SIZE = 30;

  const [board, setBoard] = useState<Board>(
    Array(BOARD_SIZE)
      .fill(null)
      .map(() => Array(BOARD_SIZE).fill(null))
  );

  const [gameId, setGameId] = useState<string>("");
  const [tmpGameId, setTmpGameId] = useState<string>("");
  const [player, setPlayer] = useState<number>(Piece.Black);
  const [turn, setTurn] = useState<number>(1);
  const [winner, setWinner] = useState<number | null>(null);
  const [isNewGame, setisNewGame] = useState<number>(0);


  useEffect(() => {
    // Fetch the game state from the server
    let id = localStorage.getItem("gameId") 
    if (id !== null) {
      setGameId(id);
    }
	}, []); // only when the component mounts for the first time

  useEffect(() => {
		if (gameId !== "" || isNewGame > 0) {
			createNewGame("");
		}
		// eslint-disable-next-line
	}, [isNewGame]);

  useEffect(() => {
		if (gameId !== "") {
			localStorage.setItem("gameId", gameId);
			getGameState();
		}
		// eslint-disable-next-line
	}, [gameId]);

  useEffect(() => {
		if (gameId !== "") {
			saveGameState();
		}
		// eslint-disable-next-line
	}, [turn]);


  const getGameState = async () => {
    await axios
			.get(`/api/game/${gameId}`)
			.then((response) => {
				const { id, board, player, winner, turn } = response.data;
				setGameId(id);
				setBoard(JSON.parse(board));
				setPlayer(player);
				setTurn(turn);
				setWinner(winner);
			})
			.catch((error) => {
				console.error(
					"Error fetching game state; creating new game with id: ",
					gameId,
					error
				);
				createNewGame(gameId);
			});
  };

	const saveGameState = async () => {
		// Save the game state to the server
		await axios
			.post("/api/make-move", {
				id: gameId,
				board,
				player,
				turn,
				winner
			})
			.catch((error) => console.error("Error saving game state:", error));
	};

  const createNewGame = async (id : string) => {
    const newBoard = Array(BOARD_SIZE)
			.fill(null)
			.map(() => Array(BOARD_SIZE).fill(null));
    await axios
			.post("/api/new-game", {
				id: id,
				board: newBoard,
				player: Piece.Black,
				turn: 1,
				winner: null,
			})
			.then((response) => {
				setGameId(response.data.gameId);
			})
			.catch((error) => console.error("Error creating new game:", error));
  }

  const resetBoard = () => {
    setBoard(
      Array(BOARD_SIZE)
      .fill(null)
      .map(() => Array(BOARD_SIZE).fill(null))
    );
    setPlayer(Piece.Black);
    setTurn(1);
    setWinner(null);
  };
  
  const getNewGame = () => {
    setisNewGame(isNewGame + 1);
  }

  const checkWinner = (row: number, col: number) => {
    setTurn(turn + 1);
    let horizontal = checkHorizontal(row, col);
    let vertical = checkVertical(row, col);
    let ne_sw = checkNE_SW(row, col);
    let nw_se = checkNW_SE(row, col);

    if (horizontal[0] >= 5 || vertical[0] >= 5 || ne_sw[0] >= 5 || nw_se[0] >= 5) {
      setWinner(player);
    } else if (turn === BOARD_SIZE * BOARD_SIZE) {
      setWinner(0);
    } else {
      setPlayer(player === Piece.Black ? Piece.White : Piece.Black);
    }
  };
  
  const checkHorizontal = (row: number, col: number):[number, boolean] =>  {
    let numConnected = 1;
    let isBlocked = false;
    
    // checking left
    for (let i = 1; i < BOARD_SIZE - col; i++) {
      let checkCell = board[row][col-i];
			if (checkCell === player) {
        numConnected++;
			} else {
        if (checkCell !== null) {
          isBlocked = true;
				}
				break;
			}
		}
    
    // checking right
    for (let i = 1; i < BOARD_SIZE - col; i++) {
      let checkCell = board[row][col+i];
			if (checkCell === player) {
        numConnected++;
			} else {
        if (checkCell !== null) {
          isBlocked = true;
				}
				break;
			}
		}
    return [numConnected, isBlocked];
  };
  
  const checkVertical = (row: number, col: number):[number, boolean] => {
    let numConnected = 1;
    let isBlocked = false;

    // checking up
    for (let i = 1; i < row; i++) {
      let checkCell = board[row - i][col];
      if (checkCell === player) {
        numConnected++;
      } else {
        if (checkCell !== null) {
          isBlocked = true;
        }
        break;
      }
    }

    // checking down
    for (let i = 1; i < BOARD_SIZE - row; i++) {
      let checkCell = board[row + i][col];
      if (checkCell === player) {
        numConnected++;
      } else {
        if (checkCell !== null) {
          isBlocked = true;
        }
        break;
      }
    }
    return [numConnected, isBlocked];
  };

  const checkNE_SW = (row: number, col: number): [number, boolean] => {
		let numConnected = 1;
		let isBlocked = false;

		// checking up right (NE)
		for (let i = 1; i < Math.min(row + 1, BOARD_SIZE - col); i++) {
			let checkCell = board[row - i][col + i];
			if (checkCell === player) {
				numConnected++;
			} else {
				if (checkCell !== null) {
					isBlocked = true;
				}
				break;
			}
		}

		// checking down left (SW)
		for (let i = 1; i < Math.min(BOARD_SIZE - row, col + 1); i++) {
			let checkCell = board[row + i][col - i];
			if (checkCell === player) {
				numConnected++;
			} else {
				if (checkCell !== null) {
					isBlocked = true;
				}
				break;
			}
		}
		return [numConnected, isBlocked];
	};

  const checkNW_SE = (row: number, col: number): [number, boolean] => {
		let numConnected = 1;
		let isBlocked = false;

		// checking up left (NW)
		for (let i = 1; i < Math.min(row + 1, col + 1); i++) {
			let checkCell = board[row - i][col - i];
			if (checkCell === player) {
				numConnected++;
			} else {
				if (checkCell !== null) {
					isBlocked = true;
				}
				break;
			}
		}

		// checking down right (SE)
		for (let i = 1; i < Math.min(BOARD_SIZE - row, BOARD_SIZE - col); i++) {
			let checkCell = board[row + i][col + i];
			if (checkCell === player) {
				numConnected++;
			} else {
				if (checkCell !== null) {
					isBlocked = true;
				}
				break;
			}
		}

		return [numConnected, isBlocked];
	};


	return (
		<div className="board">
			<div className="headerInfo">
				{winner === 0 ? (
					<div>
						<h3>It's a draw!</h3>
					</div>
				) : winner !== null ? (
					<>
						<div className="playerTurn">
							<img
								className="cell"
								src={winner === Piece.Black ? blackPiece : whitePiece}
								alt="cell"
							/>
						</div>
						<h3 style={{ marginLeft: "10px" }}> wins!</h3>
					</>
				) : gameId === "" ? (
					<></>
				) : (
					<>
						<h3>Turn {turn}:</h3>
						<div className="playerTurn">
							<img
								className="cell"
								src={player === Piece.Black ? blackPiece : whitePiece}
								alt="cell"
							/>
						</div>
						<h3>'s Turn</h3>
					</>
				)}
				<div>
					<Button
						className="newGameButton"
						variant="primary"
						onClick={() => resetBoard()}
					>
						Reset Game
					</Button>
					<div style={{ display: "flex", justifyContent: "center" }}>
						Game ID: {gameId}
					</div>
				</div>
			</div>
			{gameId !== "" ? (
				<GomokuBoard
					BOARD_SIZE={BOARD_SIZE}
					CELL_SIZE={CELL_SIZE}
					board={board}
					player={player}
					winner={winner}
					setPlayer={setPlayer}
					setBoard={setBoard}
					checkWinner={checkWinner}
				/>
			) : (
				<h3>Enter the Game ID of a previous game, or click New Game to play</h3>
			)}
			<div
				style={{
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					marginTop: "10px",
				}}
			>
				<Button
					className="newGameButton"
					variant="primary"
					onClick={() => getNewGame()}
					style={{ marginRight: "10px" }}
				>
					New Game
				</Button>
				<form onSubmit={(e) => e.preventDefault()}>
					<input
						id="gameIdForm"
						type="text"
						onChange={(e) => setTmpGameId(e.target.value)}
						placeholder="Custom Game ID"
					/>
					<Button
						type="submit"
						variant="primary"
						onClick={() => {
							if (tmpGameId !== "") {
								setGameId(tmpGameId.toUpperCase());
							}
						}}
					>
						Submit
					</Button>
				</form>
			</div>
		</div>
	);
};

export default App;
